/**
 * The free / gated line for market data, written once (plan
 * docs/plans/editorial-studio-data-moat-2026-10-14.md, lane L8, grill A).
 *
 * Every public data surface (insights, skills, salary guide, reports, research)
 * quotes this module for what is free and what sits behind the price — never a
 * per-page literal. The gated copy names exactly what the Full Dataset CSV
 * contains (rows, columns, re-download), never "premium insights".
 *
 * Pure: no DB, no env, no product-catalog import, so it is safe in client
 * components, `llms.txt` (import-light by contract) and unit tests.
 */

/** Rows per ranked table on the free layer (aggregate pages, agent answers). */
export const FREE_TOP_N = 5;

/** Months of history on the free layer: the current month only. */
export const FREE_MONTHS = 1;

/** Smallest cell published on the free layer (listings behind one number). */
export const FREE_MIN_CELL_N = 15;

/** PostHog flag that is the SOLE switch for the free layer + depth CTA. */
export const DATA_FREE_LAYER_FLAG = "data-free-layer";

/**
 * Surfaces that link to paid depth. Value = the `utm_campaign` suffix. `api` is
 * the market API free layer (REST `/api/v1/market/*` and both MCP servers'
 * market tools): an agent answer, not a page.
 */
export const DATA_DEPTH_SURFACES = [
	"insights",
	"skills",
	"salary_guide",
	"reports",
	"research",
	"api",
] as const;

export type DataDepthSurface = (typeof DATA_DEPTH_SURFACES)[number];

type Locale = "en" | "id";
type Copy = { en: string; id: string };

/**
 * What the paid Full Dataset CSV holds beyond the free aggregates. Mirrors the
 * column list on the /data storefront (`DATASET_COLUMNS` minus `source_url`,
 * minus the enrichment columns the export documents as never populated).
 */
export const GATED_FIELDS: readonly Copy[] = [
	{
		en: "Every active listing as its own row, not an aggregate",
		id: "Setiap lowongan aktif sebagai satu baris, bukan agregat",
	},
	{
		en: "Disclosed pay per listing: minimum, maximum, currency, period",
		id: "Gaji yang dicantumkan per lowongan: minimum, maksimum, mata uang, periode",
	},
	{
		en: "AI-extracted required skills and Indonesian context per listing",
		id: "Skill wajib hasil ekstraksi AI dan konteks Indonesia per lowongan",
	},
	{
		en: "Company, location, remote type, employment type, posting date",
		id: "Perusahaan, lokasi, tipe remote, tipe kerja, tanggal posting",
	},
	{
		en: "CSV download, re-download any time after purchase",
		id: "Unduhan CSV, bisa diunduh ulang kapan saja setelah membeli",
	},
];

/** One sentence naming the free layer, built from the constants above. */
export function freeLayerSummary(locale: Locale): string {
	return locale === "en"
		? `Free here: aggregate tables for the latest ${FREE_MONTHS === 1 ? "month" : `${FREE_MONTHS} months`}, national level.`
		: `Gratis di sini: tabel agregat untuk ${FREE_MONTHS === 1 ? "bulan terakhir" : `${FREE_MONTHS} bulan terakhir`}, tingkat nasional.`;
}

/** Trim a ranked list to the free layer. */
export function freeLayerRows<T>(rows: readonly T[]): T[] {
	return rows.slice(0, FREE_TOP_N);
}

/**
 * Where the depth CTA sends a visitor: the /data storefront (Mayar checkout for
 * `dataset_full` / `dataset_commercial`), tagged so `dataset_purchases` can be
 * attributed back to the surface (`utm_campaign LIKE 'data_depth_%'`, H13).
 * Relative on purpose: the storefront lives in the reports zone and a plain
 * href navigates across zones on both apex and reports hosts.
 */
export function paidDepthUrl(
	surface: DataDepthSurface,
	locale: Locale,
): string {
	const params = new URLSearchParams({
		utm_source: "lokerdollar",
		utm_medium: "data_depth_cta",
		utm_campaign: `data_depth_${surface}`,
	});
	return `/${locale}/data?${params.toString()}`;
}

/** Public data surfaces listed for agents in llms.txt (path under /en). */
export const FREE_DATA_SURFACES: readonly { path: string; label: string }[] = [
	{ path: "/insights", label: "Insights hub: skill movers and AI-skill share" },
	{
		path: "/insights/skills",
		label: "Skills employers name most, grouped by kind",
	},
	{ path: "/salary-guide", label: "Remote salary guide by role (USD)" },
	{ path: "/reports", label: "Market reports index" },
	{ path: "/research", label: "Research notes with method and data" },
	{
		path: "/reports/indonesia-salary-benchmark",
		label: "Indonesia salary benchmark",
	},
	{ path: "/data", label: "Data hub: free 50-row sample + paid full CSV" },
];
